<template>
    <div
        v-click-outside:[clickOutsideOptions]
        class="slds-dropdown-trigger slds-dropdown-trigger_click slds-is-open"
        @click="toggleMenu"
    >
        <div
            v-if="doShowAnchor"
            ref="anchor"
        >
            <slot name="button">
                <button
                    class="slds-button slds-button_icon slds-button_icon-border-filled slds-button_icon-x-small"
                    :title="showMoreLabel"
                >
                    <MAIconInline
                        :svg="require('!html-loader!@salesforce-ux/design-system/assets/icons/action/more.svg')"
                        :assistiveText="showMoreLabel"
                        iconClass="slds-button__icon"
                    />
                </button>
            </slot>
        </div>

        <div
            v-if="doShow"
            ref="menu"
            :class="{
                'slds-dropdown_right': (dropdownRight || forceRight),
                'slds-dropdown_left': (dropdownLeft || forceLeft)
            }"
            class="slds-dropdown slds-dropdown_actions"
            @mouseover="mouseover"
            @mouseout="mouseout"
        >
            <ul
                class="slds-dropdown__list"
                role="menu"
            >
                <slot>--- menu items go here ---</slot>
            </ul>
        </div>
    </div>
</template>

<script>
    import clickOutside from '../directives/click-outside';
    import MAIconInline from './ma-icon-inline';

    export default {
        name: 'MAActionMenu',
        components: { MAIconInline },
        directives: {
            'click-outside': clickOutside
        },
        props: {
            forceRight: {
                type: Boolean,
                default: false
            },
            forceLeft: {
                type: Boolean,
                default: false
            },
            doShowSync: {
                type: Boolean,
                default: false
            },
            hideOnMouseout: {
                type: Boolean,
                default: false
            },
            /**
             * If defined, this will get called before toggling the action menu and be passed the following:
             * 1. JS click event
             * 2. Current visibility state of the action menu
             *
             * If the function returns a boolean value of true then the action menu will be toggled, otherwise, nothing will occur
             */
            isToggleValid: {
                type: Function,
                default: null
            },
            // TODO - Setup a watcher on bindTo. If changed, remove existing event listener and register a new one.
            bindTo: {
                type: HTMLElement,
                default: null
            },
            bindToMousePosition: {
                type: Boolean,
                default: false,
                validate: value => (value && this.bindTo != null) || !value
            },
            bindToCoords: {
                type: Object,
                default: null,
                validate: value => value.x && value.y
            },
            isDisabled: {
                type: Boolean,
                default: false
            },
            showMoreLabel: {
                type: String,
                default: 'Show More'
            }
        },
        data() {
            return {
                doShow: this.doShowSync,
                isDirty: false,
                dropdownLeft: true,
                dropdownRight: false,
                container: null
            };
        },
        computed: {
            doShowAnchor() {
                return !(this.bindTo || this.bindToMousePosition || this.bindToCoords);
            },
            clickOutsideOptions() {
                return {
                    active: this.doShow,
                    handler: this.doHide
                };
            }
        },
        watch: {
            doShow(value) {
                this.$emit('update:doShowSync', value);
            },
            doShowSync(value) {
                if (value === this.doShow) return;
                this.toggleMenu();
            }
        },
        created() {
            if (this.bindTo) this.bindTo.addEventListener('click', this.toggleMenu);
        },
        beforeDestroy() {
            if (this.bindTo) this.bindTo.removeEventListener('click', this.toggleMenu);
            this.destroy();
        },
        methods: {
            toggleMenu(e) {
                if (this.isDisabled
                    || (e && this.isToggleValid && !this.isToggleValid(e, this.doShow))
                    || (e && this.$refs.menu && (this.$refs.menu === e.target || this.$refs.menu.contains(e.target)))
                ) return;

                this.doShow = !this.doShow;

                if (this.doShow) {
                    window.addEventListener('scroll', this.doHide, true);
                    window.addEventListener('resize', this.doHide);
                    this.$nextTick(this.showMenu.bind(this, e));
                    this.$emit('bindToClick', e);
                } else {
                    this.destroy();
                }
            },
            showMenu(e) {
                if (this.forceLeft || this.forceRight) return;

                // reset state
                this.dropdownLeft = true;
                this.dropdownRight = false;

                this.$nextTick(() => {
                    this.detach(e);
                    this.$nextTick(this.adjustForClipping);
                });
            },
            detach(e) {
                if (!this.$refs.menu) return;

                let top;
                let left;

                if (e && this.bindToMousePosition) {
                    top = e.clientY;
                    left = e.clientX;
                } else if (this.bindToCoords) {
                    top = this.bindToCoords.y;
                    left = this.bindToCoords.x;
                } else {
                    const anchor = this.$refs.anchor.getBoundingClientRect();
                    top = anchor.top + this.$refs.anchor.clientHeight;
                    ({ left } = anchor);
                }

                this.$refs.menu.style.top = `${top}px`;
                this.$refs.menu.style.left = `${left}px`;
                this.$refs.menu.style.position = 'fixed';

                this.container = document.createElement('div');
                this.container.className = `slds-scope ${this.$el.className}`;
                this.container.appendChild(this.$refs.menu);
                document.body.appendChild(this.container);

                this.$emit('detached', this.container);

                window.addEventListener('scroll', this.doHide, true);
            },
            adjustForClipping() {
                const { menu } = this.$refs;
                if (menu == null) return;
                const menuWidth = menu.clientWidth;
                const menuHeight = menu.clientHeight;
                const menuPositionX = menu.getBoundingClientRect().x;
                const menuPositionY = menu.getBoundingClientRect().y;
                const { clientWidth } = document.documentElement;
                const { clientHeight } = document.documentElement;
                const doesOverflowX = (menuPositionX + menuWidth > clientWidth);
                const doesOverflowY = (menuPositionY + menuHeight > clientHeight);

                if (doesOverflowX) {
                    this.$refs.menu.style.left = 'auto';
                }

                if (doesOverflowY) {
                    this.$refs.menu.style.top = 'auto';
                    this.$refs.menu.style.bottom = '0';
                }

                this.dropdownRight = doesOverflowX;
                this.dropdownLeft = !doesOverflowX;
            },
            destroy() {
                this.doShow = false;
                window.removeEventListener('scroll', this.doHide, true);
                window.removeEventListener('resize', this.doHide);

                if (this.container) {
                    this.container.remove();
                    this.container = null;
                }

                this.$emit('hide');
            },
            doHide(e) {
                if (!this.doShow || (this.$refs.menu && (this.$refs.menu === e.target || (this.bindTo && this.bindTo.contains(e.target))))) return;
                this.destroy();
            },
            mouseover(e) {
                this.$emit('mouseover', e);
            },
            mouseout(e) {
                if (this.hideOnMouseout && e.type === 'mouseout' && !this.$refs.menu.contains(e.toElement || e.relatedTarget)) {
                    this.destroy();
                    return;
                }

                this.$emit('mouseout', e);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .slds-dropdown {
        z-index: 10000;

        >>> .slds-dropdown__header {
            font-size: .875rem;
            font-weight: 700;
            padding: .5rem .75rem;
        }
    }
</style>
